import { i18n } from "@/locales";
import { useUISlice } from "@/models/useUISlice";
import { actions, useAppStore } from "@/controller";

export type AiInstructionsFeature = "summary" | "quiz" | "flashcard";

// Shared "regenerate with instructions" prompt. Prefilled with the user's
// saved default for the feature (or the last-used text), and the "use by
// default" switch saves the instructions to the user profile so the server
// applies them to future first-time generations.
export const promptAiInstructions = (
  feature: AiInstructionsFeature,
  onSubmit: (instructions: string) => void
): void => {
  const savedDefault =
    useAppStore.getState().userData?.preferences?.aiInstructions?.[feature];

  const setToggle = (next: boolean) => {
    const current = useUISlice.getState().alertPrompt;
    if (!current?.toggle) return;
    useUISlice.getState().setAlertPrompt({
      ...current,
      toggle: { ...current.toggle, value: next },
    });
  };

  useUISlice.getState().setAlertPrompt({
    title: i18n.t(`aiInstructions.${feature}Title`),
    subtitle: i18n.t("aiInstructions.subtitle"),
    placeholder: i18n.t(`aiInstructions.${feature}Placeholder`),
    defaultValue:
      savedDefault || useAppStore.getState().lastAiInstructions?.[feature] || "",
    multiLine: true,
    toggle: {
      label: i18n.t("aiInstructions.defaultToggle"),
      value: Boolean(savedDefault),
      onChange: setToggle,
    },
    button: {
      text: i18n.t("aiInstructions.button"),
      onPress: async (text) => {
        const instructions = text.trim();
        if (!instructions) return;
        const makeDefault = Boolean(
          useUISlice.getState().alertPrompt?.toggle?.value
        );
        useAppStore.getState().setLastAiInstructions(feature, instructions);
        useUISlice.getState().setAlertPrompt(undefined);

        // Persist or clear the per-user default. Preferences replace
        // wholesale server-side, so send the full current object.
        const shouldClear = !makeDefault && Boolean(savedDefault);
        if (makeDefault || shouldClear) {
          const preferences =
            useAppStore.getState().userData?.preferences || {};
          const aiInstructions = { ...preferences.aiInstructions };
          if (makeDefault) aiInstructions[feature] = instructions;
          else delete aiInstructions[feature];
          void actions.user
            .update({ preferences: { ...preferences, aiInstructions } })
            .catch(() => undefined);
        }

        onSubmit(instructions);
      },
    },
  });
};
